import React, { useEffect } from 'react';
import { 
  Box, 
  Button, 
  Container, 
  Typography, 
  Grid, 
  Paper, 
  Card, 
  CardContent, 
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack
} from '@mui/material';
import { 
   
  CheckCircleOutline as CheckIcon,
  LocalHospital as HospitalIcon,
  Map as MapIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { logEvent } from '../firebase/config';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();

  // Initialize Google Analytics and track page view
  useEffect(() => {
    // Track page view when component mounts
    logEvent('page_view', {
      page_title: 'Landing Page',
      page_location: window.location.href,
      page_path: window.location.pathname
    });
  }, []);

  const handleLoginClick = () => {
    // Log click event
    logEvent('login_button_click');
    navigate('/dashboard/login');
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      bgcolor: '#f5f5f5'
    }}>
      {/* Hero Section */}
      <Box sx={{ 
        bgcolor: 'primary.main', 
        color: 'white',
        py: 8,
        backgroundImage: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography variant="h2" component="h1" gutterBottom fontWeight="bold">
                TB Treatment Adherence Monitoring System
              </Typography>
              <Typography variant="h5" paragraph sx={{ mb: 4, opacity: 0.9 }}>
                A comprehensive solution for tracking, monitoring, and managing tuberculosis cases across the Philippines
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleLoginClick}
                >
                  Login
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  size="large"
                  onClick={() => navigate('/signup')}
                >
                  Sign Up
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={5}>
              <Paper elevation={10} sx={{ 
                borderRadius: 4, 
                overflow: 'hidden',
                transform: 'perspective(1500px) rotateY(-10deg)',
                boxShadow: '0 25px 50px -12px rgba(0,0,0,0.4)',
              }}>
                <Box
                  component="img"
                  src="/tb-dashboard-preview.JPG"
                  alt="TB Dashboard Preview"
                  sx={{ 
                    width: '100%',
                    height: 'auto',
                    display: 'block'
                  }}
              
                />
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container sx={{ py: 8 }}>
        <Typography variant="h3" component="h2" align="center" gutterBottom fontWeight="medium">
          Key Features
        </Typography>
        <Typography variant="h6" align="center" color="textSecondary" paragraph sx={{ mb: 6 }}>
          Our system provides powerful tools for everyone involved in TB care
        </Typography>

        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', borderRadius: 2, boxShadow: 3, transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
              <CardContent>
                <Box sx={{ mb: 2, color: 'primary.main' }}>
                  <MapIcon fontSize="large" />
                </Box>
                <Typography variant="h5" component="h3" gutterBottom>
                  For Healthcare Providers
                </Typography>
                <Typography variant="body1" color="textSecondary" paragraph>
                  Access comprehensive dashboard with geospatial analysis, patient management, and treatment monitoring tools
                </Typography>
           
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', borderRadius: 2, boxShadow: 3, transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
              <CardContent>
                <Box sx={{ mb: 2, color: 'primary.main' }}>
                  <HospitalIcon fontSize="large" />
                </Box>
                <Typography variant="h5" component="h3" gutterBottom>
                  For Patients
                </Typography>
                <Typography variant="body1" color="textSecondary" paragraph>
                  Track medication schedule, appointments, and symptoms with an easy-to-use interface designed for TB patients
                </Typography>
            
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', borderRadius: 2, boxShadow: 3, transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
              <CardContent>
                <Box sx={{ mb: 2, color: 'primary.main' }}>
                  <AssessmentIcon fontSize="large" />
                </Box>
                <Typography variant="h5" component="h3" gutterBottom>
                  For Caregivers & Family
                </Typography>
                <Typography variant="body1" color="textSecondary" paragraph>
                  Support loved ones by monitoring treatment progress, receiving reminders, and communicating with healthcare providers
                </Typography>
                
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Statistics Section */}
      <Box sx={{ bgcolor: 'secondary.light', py: 8 }}>
        <Container>
          <Typography variant="h3" component="h2" align="center" gutterBottom>
            Making an Impact
          </Typography>
          <Typography variant="h6" align="center" color="textSecondary" paragraph sx={{ mb: 6 }}>
            Our system is helping healthcare professionals combat tuberculosis across the Philippines
          </Typography>

          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={6} md={3}>
              <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
                <Typography variant="h3" color="primary.main" fontWeight="bold">
                  30+
                </Typography>
                <Typography variant="body1">
                  Provinces Covered
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={3}>
              <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
                <Typography variant="h3" color="primary.main" fontWeight="bold">
                  1,000+
                </Typography>
                <Typography variant="body1">
                  Patients Tracked
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={3}>
              <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
                <Typography variant="h3" color="primary.main" fontWeight="bold">
                  95%
                </Typography>
                <Typography variant="body1">
                  Adherence Rate
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={3}>
              <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
                <Typography variant="h3" color="primary.main" fontWeight="bold">
                  24/7
                </Typography>
                <Typography variant="body1">
                  Monitoring
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Container sx={{ py: 8 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h3" component="h2" gutterBottom>
              Helping Fight Tuberculosis
            </Typography>
            <Typography variant="body1" paragraph color="textSecondary">
              Our TB Treatment Adherence Monitoring System provides healthcare professionals with the tools they need to effectively track and manage tuberculosis cases, improving patient outcomes and reducing the spread of TB.
            </Typography>
            
            <List>
              {[
                'Real-time monitoring of patient adherence to medication regimens',
                'Identification of TB hotspots through geospatial visualization',
                'Comprehensive patient records including treatment history',
                'Streamlined appointment scheduling and follow-up management',
                'Data-driven insights to guide public health interventions'
              ].map((benefit, index) => (
                <ListItem key={index} sx={{ py: 1 }}>
                  <ListItemIcon>
                    <CheckIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary={benefit} />
                </ListItem>
              ))}
            </List>
            
            <Button 
              variant="contained" 
              color="primary" 
              size="large"
              onClick={handleLoginClick}
              sx={{ mt: 2 }}
            >
              Access Dashboard
            </Button>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ 
              borderRadius: 4, 
              overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            }}>
              <Box
                component="img"
                src="/tb-management.JPG"
                alt="TB Management"
                sx={{ 
                  width: '100%',
                  height: 'auto',
                  display: 'block'
                }}
                
              />
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Footer */}
      <Box sx={{ bgcolor: 'primary.dark', color: 'white', py: 6, mt: 6 }}>
        <Container>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                TB Treatment Adherence Monitoring System
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                A comprehensive solution for tracking, monitoring, and managing tuberculosis cases across the Philippines.
                Together we can help end TB.
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="h6" gutterBottom>
                Contact
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Email: tbxhealthtech@gmail.com<br />
                Phone: +63 (2) 8651-7800<br />
                National TB Control Program, Department of Health<br />
                Manila, Philippines
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="h6" gutterBottom>
                Quick Links
              </Typography>
              <Box component="nav">
                <Button color="inherit" onClick={handleLoginClick}>Login</Button><br />
                <Button color="inherit" href="https://ntp.doh.gov.ph" target="_blank">DOH TB Program</Button><br />
                <Button color="inherit" href="https://www.who.int/health-topics/tuberculosis" target="_blank">WHO TB Info</Button>
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ mt: 6, textAlign: 'center', opacity: 0.6 }}>
            <Typography variant="body2">
              © {new Date().getFullYear()} TB Treatment Adherence Monitoring System. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage; 